import { createAsyncThunk } from '@reduxjs/toolkit';
import type { HttpError } from '@wix/yoshi-flow-bm';
import { listAdUnits } from '@wix/ambassador-adsense-v1-adsense-adunit/http';
import {
  State,
  type AdUnit,
  type ListAdUnitsResponse,
} from '@wix/ambassador-adsense-v1-adsense-adunit/types';
import { type StoreExtraArgs } from '@app/store/settings/types';

// Google's AdSense API has no server-side search of its own, and realistic
// accounts have hundreds (not thousands) of ad units, so the full list is
// fetched once - looping pageToken internally - rather than paginated
// 10-at-a-time on scroll. Each Google page already supports up to this
// many results (see ListAdUnitsRequest in
// packages/adsense-proto/src/main/proto/wix/labs/adsense/v1/adunit/adsense_adunit_service.proto).
const FETCH_ALL_PAGE_SIZE = 10000 as const;

export const fetchAdUnitsList = createAsyncThunk(
  'fetchAdUnitsList/GET',
  async (_, { extra, rejectWithValue }): Promise<ListAdUnitsResponse> => {
    const { httpClient, wixSdk } = extra as StoreExtraArgs;
    const instanceId = wixSdk.getInstanceId();

    try {
      const adUnits: AdUnit[] = [];
      let pageToken: string | undefined;
      do {
        const { data } = await httpClient.request(
          listAdUnits({ instanceId, pageSize: FETCH_ALL_PAGE_SIZE, pageToken }),
        );
        adUnits.push(
          ...(data.adUnits?.filter((adUnit) => adUnit.state === State.ACTIVE) ??
            []),
        );
        pageToken = data.nextPageToken || undefined;
      } while (pageToken);

      return { adUnits };
    } catch (e) {
      const error = e as HttpError;
      const message = error.response?.data?.message || error.message;
      throw rejectWithValue(message);
    }
  },
);
